import {Auth} from '@aws-amplify/auth';
import {ThunkDispatch} from '@reduxjs/toolkit';
import {Account} from '../../accounts/types';
import {update} from '../../accounts/services/AccountsService';
import {fetchAccounts} from '../../accounts/reducers/recordsSlice';
import {store} from '../../../store';
import {ActivityEventsService} from '../../debug-mode/activity-events/services/ActivityEventsService';

/**
 * Clears local authentication tokens for an account whose session is already invalid.
 * Unlike handleSignOut in useAccountActions, this:
 * - Does NOT call Auth.signOut({ global: true }) (session is already invalidated server-side)
 * - Does NOT require useNavigate / Router context
 * - Only clears local Cognito cache and updates the account timestamp
 */
export const clearInvalidSession = async (account: Account): Promise<void> => {
    try {
        Auth.configure({
            userPoolId: account.cognitoUserPoolId,
            userPoolWebClientId: account.appClientId,
        });

        // Local-only sign out — don't attempt global revocation on an already-invalid session
        try {
            await Auth.signOut();
        } catch (e) {
            // Token may already be gone, that's fine
            console.log('LOGIN APP. CLEAR SESSION. Local signOut threw (expected if tokens already gone)', account.emailAddress);
        }

        ActivityEventsService.add(
            'Session Auto-Cleared',
            `Auto-cleared invalid session for: ${account.emailAddress}`,
            account.emailAddress
        );

        const updated = Object.assign({}, account, {updatedAt: new Date().toISOString()});
        await update(account.id, updated);

        // Refresh Redux state from Cache
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const dispatch = store.dispatch as ThunkDispatch<any, any, any>;
        dispatch(fetchAccounts());
    } catch (error) {
        console.trace('LOGIN APP. CLEAR SESSION ERROR', account.emailAddress, error);
    }
};
